import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CancelSubscriptionModalProps } from './types';
import { formatDate, formatPrice, getCurrency } from './utils';
import './styles/index.less';

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  visible,
  subscription,
  onCancel,
  onConfirm,
  loading = false,
  currentPlanData,
  subscriptionProducts = [],
}) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    console.log('currentPlanData', currentPlanData);
    console.log('subscription', subscription);
  }, [currentPlanData, subscription]);

  if (!visible) return null;

  // 获取当前订阅的真实价格信息
  const getCurrentSubscriptionPrice = () => {
    if (!currentPlanData?.plan?.product_id || !subscriptionProducts.length) {
      return subscription.amount; // 回退到传入的金额
    }

    const productId = currentPlanData.plan.product_id;
    const currentProduct = subscriptionProducts.find((product) => product.product_id === productId);
    if (!currentProduct) return subscription.amount;

    const currency = getCurrency(i18n.language);
    const priceInfo = currentProduct.price_list.find((price: any) => price.currency === currency);

    return priceInfo?.price || subscription.amount;
  };

  // 获取当前订阅的计划名称
  const getCurrentPlanName = () => {
    if (!currentPlanData?.plan?.product_id) {
      return subscription.plan; // 回退到传入的计划名称
    }

    const productId = currentPlanData.plan.product_id;
    const currentProduct = subscriptionProducts.find(product => product.product_id === productId);
    return currentProduct?.name.split(' ').at(-1) || productId.split('_').at(-1);
  };

  // 获取订阅类型（月付/年付）
  const getSubscriptionType = () => {
    if (currentPlanData?.plan?.subscription_type) {
      return currentPlanData.plan.subscription_type === 'MONTHLY' ? 'month' : 'year';
    }
    return subscription.interval;
  };

  const currentPrice = getCurrentSubscriptionPrice();
  const currentPlanName = getCurrentPlanName();
  const subscriptionType = getSubscriptionType();
  const currency = getCurrency(i18n.language);

  return (
    <div className="cancel-subscription-modal">
      {/* 背景遮罩 */}
      <div className="modal-backdrop" onClick={onCancel}></div>

      {/* 模态框内容 */}
      <div className="modal-container">
        <div className="modal-content">
          {/* 模态框头部 */}
          <div className="modal-header">
            <div className="warning-icon-container">
              <div className="warning-icon">
                <div className="icon-background">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
                <div className="icon-glow"></div>
              </div>
            </div>
            <div className="header-content">
              <h3 className="modal-title">
                {t('account.cancelSubscription') || 'Cancel Subscription'}
              </h3>
              <p className="modal-subtitle">
                {t('account.confirmYourDecision') || 'Please confirm your decision'}
              </p>
            </div>
            <button className="close-button" onClick={onCancel}>
              <div className="close-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </div>
            </button>
          </div>

          {/* 模态框主体 */}
          <div className="modal-body">
            <div className="warning-message">
              <div className="message-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6"/>
                  <path d="M12 18h.01"/>
                </svg>
              </div>
              <p className="primary-text">
                {t('account.cancelSubscriptionConfirmation', {
                  date: formatDate(currentPlanData?.plan?.reset_time),
                })
                  || `Are you sure you want to cancel your subscription? You'll continue to have access until your current billing period ends on ${formatDate(subscription.currentPeriodEnd)}.`}
              </p>
            </div>

            {/* 订阅信息卡片 */}
            <div className="subscription-info-card">
              <div className="card-header">
                <div className="card-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <div className="card-title">{t('account.currentSubscriptionInfo') || 'Current Subscription Info'}</div>
              </div>

              <div className="info-content">
                <div className="plan-info">
                  <div className="plan-name">{currentPlanName}</div>
                  <div className="plan-details">
                    <span className="plan-period">
                      {subscriptionType === 'month' ? t('account.monthly') || 'Monthly' : t('account.yearly') || 'Yearly'}
                    </span>
                    <span className="plan-price">{formatPrice(currentPrice, currency)}</span>
                  </div>
                </div>

                <div className="access-info">
                  <div className="access-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4"/>
                      <path d="M21 12c-1 0-2-1-2-2s1-2 2-2 2 1 2 2-1 2-2 2z"/>
                      <path d="M3 12c1 0 2-1 2-2s-1-2-2-2-2 1-2 2 1 2 2 2z"/>
                      <path d="M12 3c0 1-1 2-2 2s-2-1-2-2 1-2 2-2 2 1 2 2z"/>
                      <path d="M12 21c0-1 1-2 2-2s2 1 2 2-1 2-2 2-2-1-2-2z"/>
                    </svg>
                  </div>
                  <div className="access-text">
                    <span className="access-label">{t('account.continueFullAccessUntil') || 'Continue to have full access until'}</span>
                    <span className="access-date">{formatDate(currentPlanData?.plan?.reset_time)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 影响提示 */}
            <div className="impact-notice">
              <div className="notice-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <div className="notice-content">
                <h4>{t('account.impactAfterCancellation') || 'Impact after cancellation'}</h4>
                <ul>
                  <li>{t('account.continueAllFeaturesCurrentBilling') || 'Continue to have all features during current billing cycle'}</li>
                  <li>{t('account.noPaidFeaturesNextBilling') || 'Paid features will be unavailable starting next billing cycle'}</li>
                  <li>{t('account.canResubscribeAnytime') || 'You can resubscribe anytime to restore service'}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 模态框操作按钮 */}
          <div className="modal-actions">
            <button
              className="action-button keep-button"
              onClick={onCancel}
              disabled={loading}
            >
              <div className="button-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <span className="button-text">{t('account.keepSubscription') || 'Keep Subscription'}</span>
            </button>
            <button
              className={`action-button cancel-button ${loading ? 'loading' : ''}`}
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  <span className="button-text">{t('account.processing') || 'Processing...'}</span>
                </>
              ) : (
                <>
                  <div className="button-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </div>
                  <span className="button-text">{t('account.cancelSubscription') || 'Cancel Subscription'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;
